import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = [
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-1.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-2.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-3.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-4.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-5.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-6.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-7.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-8.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-9.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-10.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-11.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-12.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-13.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-14.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-15.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-16.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-17.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-18.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-19.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-20.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-21.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-22.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-23.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-24.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-25.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-26.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-27.png',
    'https://nxnsqlrnauuqcegihtys.supabase.co/storage/v1/object/public/property-images/gallery/gallery-28.png'
  ];
  
  const containerRef = useRef<HTMLDivElement>(null);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const [slideWidth, setSlideWidth] = useState(0);
  
  useEffect(() => {
    const updateLayout = () => {
      if (window.innerWidth >= 1024) {
        setSlidesToShow(4);
      } else if (window.innerWidth >= 640) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(1);
      }
      
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const gap = 16; // 1rem gap
        const width = (containerWidth - (gap * (slidesToShow - 1))) / slidesToShow;
        setSlideWidth(width);
      }
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, [slidesToShow]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      Math.min(prevIndex + 1, images.length - slidesToShow)
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      Math.max(prevIndex - 1, 0)
    );
  };

  return (
    <div className="relative w-full overflow-hidden px-4">
      <button 
        onClick={prevSlide}
        disabled={currentIndex === 0}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 text-gold hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Previous slides"
      >
        <ChevronLeft size={40} />
      </button>
      
      <button 
        onClick={nextSlide}
        disabled={currentIndex >= images.length - slidesToShow}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/50 text-gold hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Next slides"
      >
        <ChevronRight size={40} />
      </button>

      <div 
        ref={containerRef}
        className="flex transition-transform duration-500 ease-out"
        style={{
          transform: `translateX(-${currentIndex * (slideWidth + 16)}px)`,
        }}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className="flex-none"
            style={{ 
              width: `${slideWidth}px`,
              marginRight: index < images.length - 1 ? '1rem' : 0
            }}
          >
            <div className="h-64 rounded-lg overflow-hidden transition-transform duration-300 hover:scale-105">
              <img 
                src={image} 
                alt={`Gallery image ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Gallery;